import { Box, Button, Stack, Typography } from '@mui/material'
import { POSE_ENTER_DEG, type PoseView } from '@/lib/gaze/headPose'

// ---- Pose guidance screen ----------------------------------------------------
//
// Shared by the admin lab and the respondent calibration: the ring is the pose
// target, the dot is the measured head, and the ring fills while the head holds.

const METER_HALF_PX = 110
const METER_RANGE_DEG = 30

/** Per-axis hint, the sandbox's show_pose_target in words. */
function axisHint(off: number, more: string, less: string): { text: string; ok: boolean } {
  if (Math.abs(off) <= POSE_ENTER_DEG) return { text: 'ok', ok: true }
  return { text: `${off > 0 ? more : less} o ${Math.round(Math.abs(off))}°`, ok: false }
}

export default function PoseGuide({
  view,
  title,
  hint,
  counter,
  accent,
  onSkip,
  skipLabel = 'Pomiń tę pozę',
  onCancel,
}: {
  view: PoseView
  title: string
  /** Instruction under the title once the baseline exists. */
  hint: string
  counter: string | null
  accent: string
  /** Omitted → no skip button. */
  onSkip?: () => void
  skipLabel?: string
  /** Omitted → no cancel button. */
  onCancel?: () => void
}) {
  const scale = METER_HALF_PX / METER_RANGE_DEG
  const clampPx = (v: number) => Math.max(-METER_HALF_PX, Math.min(METER_HALF_PX, v))
  // Mirror yaw so the marker moves the way the head does from the user's seat
  // (yaw > 0 = turned to their left → marker goes left).
  const mx = view.off ? clampPx(-view.off.yaw * scale) : 0
  const my = view.off ? clampPx(view.off.pitch * scale) : 0
  const yawHint = view.off ? axisHint(view.off.yaw, 'w prawo', 'w lewo') : null
  const pitchHint = view.off ? axisHint(view.off.pitch, 'unieś', 'opuść') : null
  const zonePx = POSE_ENTER_DEG * scale * 2

  return (
    <Box sx={{ height: '100%', display: 'grid', placeItems: 'center', color: '#fff', px: 2 }}>
      <Stack spacing={2.5} sx={{ alignItems: 'center', textAlign: 'center', maxWidth: 520 }}>
        {counter && (
          <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', letterSpacing: 0.4 }}>
            {counter}
          </Typography>
        )}
        <Typography sx={{ fontSize: 28, fontWeight: 800 }}>{title}</Typography>
        <Typography sx={{ fontSize: 15, color: 'rgba(255,255,255,0.7)' }}>
          {view.establishing
            ? 'Patrz na środek ekranu, głowa prosto i nieruchomo — to ustawienie staje się punktem zero dla kolejnych póz.'
            : hint}
        </Typography>

        {/* Meter: the ring is the target, the dot is your head. */}
        <Box
          sx={{
            position: 'relative',
            width: METER_HALF_PX * 2,
            height: METER_HALF_PX * 2,
            borderRadius: '20px',
            bgcolor: 'rgba(0,0,0,0.25)',
            border: '1px solid rgba(255,255,255,0.12)',
          }}
        >
          <Box sx={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: '1px', bgcolor: 'rgba(255,255,255,0.1)' }} />
          <Box sx={{ position: 'absolute', top: '50%', left: 0, right: 0, height: '1px', bgcolor: 'rgba(255,255,255,0.1)' }} />
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              width: zonePx,
              height: zonePx,
              transform: 'translate(-50%, -50%)',
              borderRadius: '50%',
              border: `2px dashed ${view.inside ? '#4ade80' : 'rgba(255,255,255,0.45)'}`,
              background: view.inside
                ? `conic-gradient(rgba(74,222,128,0.45) ${view.progress * 360}deg, transparent 0deg)`
                : 'transparent',
            }}
          />
          {view.off && (
            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                width: 18,
                height: 18,
                borderRadius: '50%',
                bgcolor: view.inside ? '#4ade80' : accent,
                border: '2px solid #fff',
                transform: `translate(calc(-50% + ${mx}px), calc(-50% + ${my}px))`,
                transition: 'transform 60ms linear',
              }}
            />
          )}
        </Box>

        <Box sx={{ minHeight: 48, fontFamily: 'ui-monospace, monospace', fontSize: 15 }}>
          {!view.face && <Box sx={{ color: '#fca5a5' }}>Nie widzę twarzy — wróć przed kamerę</Box>}
          {view.face && view.establishing && (
            <Box sx={{ color: view.inside ? '#4ade80' : 'rgba(255,255,255,0.8)' }}>
              {view.inside ? 'Trzymaj…' : 'Ustaw głowę prosto i nie ruszaj się'}
            </Box>
          )}
          {view.face && !view.establishing && yawHint && pitchHint && (
            <>
              <Box sx={{ color: yawHint.ok ? '#4ade80' : '#fff' }}>
                obrót: {yawHint.ok ? 'ok' : yawHint.text}
              </Box>
              <Box sx={{ color: pitchHint.ok ? '#4ade80' : '#fff' }}>
                pochylenie: {pitchHint.ok ? 'ok' : pitchHint.text}
              </Box>
            </>
          )}
        </Box>

        {(onSkip || onCancel) && (
          <Stack direction="row" spacing={1.5}>
            {onSkip && (
              <Button onClick={onSkip} sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.6)' }}>
                {skipLabel}
              </Button>
            )}
            {onCancel && (
              <Button onClick={onCancel} sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.6)' }}>
                Przerwij (Esc)
              </Button>
            )}
          </Stack>
        )}
      </Stack>
    </Box>
  )
}
